import api from '@/api'

import moment from 'moment'
import { EditableFillgridColumnDates, FillgridColumn, FillgridEntry, FillgridFilters, FillgridSearchFilters, FillgridViewFilters, FillStatuses, TemplateQuestion, TemplateQuestionsTypes } from '@/types/Fillgrid'
import { CursorRequest, CursorResponse, PageRequest, PageResponse } from '@/types/Support'
import userInfoStore from '@/store/userInfo'
import config from '@/config'
import { CustomSearch } from '@/types/catalogs/CustomSearch'
import { CustomView } from '@/types/catalogs/Views'
import { DateUtils } from './DateFormat'
import LogService from './support/LogService'
import { Template } from '@/types/Templates'
import { ConfigurationResponse } from '@/types/Configuration'
import Normalizer from '@/types/Normalizer'
import { AxiosResponse } from 'axios'

const LAST_FILLGRID_SEARCH = 'last_fillgrid_search'
const DATE_FILTER_FORMAT = 'YYYY-MM-DDTHH:mm:ss'
const QUESTION_GROUP_PIVOT_REGEXP = /(\((\b(?!__ROLE__\b)\w+)\)(==|!=|<|>|<>|<=|>=|~|~=|:)\((("?\w"?|\.)+)\))|((\b(?!__ROLE__\b)\w+)(==|!=|<|>|<>|<=|>=|~|~=|:)(("?\w"?|\.)+))/g
const FILLGRID_ORIGIN = 'misTareas'

class FillgridService {
  /**
   * Gets a list with all configurations from the api_configuration table
   * Ex.
   * {
   *   configuration: [
   *    {
   *        "key": "mis_tareas_fts",
   *        "value": "50"
   *    },
   *    {
   *        "key": "mis_tareas_getAll",
   *        "value": "50"
   *    }
   *  ]
   * }
   * @returns configuration list
   */
  async getApiConfiguration (): Promise<ConfigurationResponse> {
    try {
      const response = await api.get(`${config.apiUsersUrl}configuration`)
      return response.data
    } catch (error) {
      LogService.error('Error getting configuration: ' + error)
      throw error
    }
  }

  async getFills (
    templateId: number,
    cursorRequest: CursorRequest<FillgridFilters>,
    cursor?: string,
    customSearchId?: number,
    requestByFTS: boolean = false,
    count: boolean = false
  ): Promise<CursorResponse<FillgridEntry>> {
    // All searches with a fullSearch, geoDistance or 'Todo' date range filter, must be send to FTS
    if (
      this.existGeoDistanceFilter(cursorRequest.filters) ||
      this.existFullSearchFilter(cursorRequest.filters) ||
      this.isAllDateRange(cursorRequest.filters)
    ) {
      // Query by ElasticSearch
      const data = this.preparePOSTData(cursorRequest, count, cursor)
      const response = await api.post(`${config.apiFillsUrl}templates/${templateId}/fills/fts`, data)
      return response.data
    } else if (this.existIdentsFilter(cursorRequest.filters) || this.existUserFilter(cursorRequest.filters)) {
      if (requestByFTS || this.existsOnlyIdent2InIdentFilters(cursorRequest.filters)) {
        // Query by ElasticSearch
        const data = this.preparePOSTData(cursorRequest, count, cursor)
        const response = await api.post(`${config.apiFillsUrl}templates/${templateId}/fills/fts`, data)
        return response.data
      } else {
        // Query by Database
        const params = this.prepareGETParams(cursorRequest, count, cursor, customSearchId)
        const response = await api.get(`${config.apiFillsUrl}templates/${templateId}/fills`, { params })
        return response.data
      }
    } else {
      const params = this.prepareGETParams(cursorRequest, count, cursor, customSearchId)
      const response = await api.get(`${config.apiFillsUrl}templates/${templateId}/fills`, { params })
      return response.data
    }
  }

  async getCountFills (
    templateId: number,
    cursorRequest: CursorRequest<FillgridFilters>,
    cursor?: string,
    customSearchId?: number,
    requestByFTS: boolean = false
  ): Promise<number> {
    const count = true
    const response = await this.getFills(templateId, cursorRequest, cursor, customSearchId, requestByFTS, count)
    return response.total
  }

  isAllDateRange (filters: FillgridFilters) {
    // Assume the range 'Todo' was selected if there is no date range selected
    return filters.dates.length === 0
  }

  existIdentsFilter (filters: FillgridFilters) {
    const identIndexes = [0, 1, 2]
    return identIndexes.some(identIndex => this.existsIdentFilterByIndex(filters, identIndex))
  }

  existsIdentFilterByIndex (filters: FillgridFilters, identIndex: number) {
    return filters.idents[identIndex] && filters.idents[identIndex].ids && filters.idents[identIndex].ids.length > 0
  }

  existsOnlyIdent2InIdentFilters (filters: FillgridFilters) {
    return this.existsIdentFilterByIndex(filters, 2) && !this.existsIdentFilterByIndex(filters, 0) && !this.existsIdentFilterByIndex(filters, 1)
  }

  existGeoDistanceFilter (filters: FillgridFilters) {
    return filters.geoDistance && filters.geoDistance?.kilometers !== 0 &&
      (filters.geoDistance?.location.latitude !== 0 || filters.geoDistance?.location.longitude !== 0)
  }

  existFullSearchFilter (filters: FillgridFilters) {
    return filters.custom.length || filters.fullSearch
  }

  existUserFilter (filters: FillgridFilters) {
    return filters.user
  }

  /**
   * Validate if the filters config have some values filled, that are:
   * - custom: customizable filters
   * - user: filter that indicates altUser o user
   * - geoDistance: filter by radio and location
   * @param filters to check
   * @returns true when the filters have some values filled, false otherwise
   */
  isFiltersConfigToFTS (filters: FillgridFilters) {
    return this.existFullSearchFilter(filters) ||
      this.existUserFilter(filters) ||
      this.existGeoDistanceFilter(filters)
  }

  /**
   * Initialize the date values.
   * This range could contain two nulls if the dates are not defined, that is when the 'Todo' range is seleced.
   *
   * @param dates selected in the filters
   * @returns an array with lenght two, the first value will be the 'from' date and the second the 'to' date,
   *  'from' date is initialize with 00:00:00 hrs if this is defined, null otherwise and
   *  'to' date has two option:
   *    it is initialize with 23:59:59 hrs if the date is different of the current date,
   *    otherwise this will have the current time. This happends when is defined if not this will be null.
   */
  private dateRangeFilter (dates: Date[]): [moment.Moment | null, moment.Moment | null] {
    const fromDate = dates.length && dates[0]
      ? moment(dates[0]).set({ 'hour': 0, 'minute': 0, 'second': 0 }).utc()
      : null

    let toDate = dates.length && dates[1]
      ? moment(dates[1])
      : null
    if (toDate?.isSame(moment(), 'day')) toDate = null
    else toDate?.set({ 'hour': 23, 'minute': 59, 'second': 59 }).utc()

    return [fromDate, toDate]
  }

  /**
   * Create a URLSearchParams to make a request in the database.
   * This URLSearchParams must be used when the request is made using the GET method with '/fills'.
   *
   * @param cursorRequest config for the request
   * @param count if the params have to consider the total in the request
   * @param cursor for the next request
   * @param customSearchId to make the request with a custom search saved in the database
   *
   * @returns a URLSearchParams to send in the GET method.
   */
  prepareGETParams (
    cursorRequest: CursorRequest<FillgridFilters>,
    count: boolean,
    cursor?: string,
    customSearchId?: number
  ) {
    const filters = cursorRequest.filters
    const params = new URLSearchParams()

    params.append('origin', FILLGRID_ORIGIN) // to identify from where is this request

    if (count) {
      params.append('limit', '1')
      params.append('count', 'true')
    } else {
      params.append('limit', cursorRequest.limit.toString())
    }
    if (cursor) {
      params.append('cursor', cursor)
    }
    if (customSearchId) {
      params.append('search', customSearchId.toString())
      return params
    }
    if (filters.view) params.append('view', filters.view.toString())
    params.append('date-type', filters.dateType)

    // Date range
    const [fromDate, toDate] = this.dateRangeFilter(filters.dates)
    if (fromDate) {
      params.append('from', fromDate.format(DATE_FILTER_FORMAT))
    }
    if (toDate) {
      params.append('to', toDate.format(DATE_FILTER_FORMAT))
    }
    if (filters.status) {
      params.append('worked', filters.status === 1 ? 'true' : 'false')
    }
    // Idents
    if (filters.idents.length) {
      filters.idents.forEach((ident, index) => {
        ident.ids.forEach((val: string) => {
          params.append('ident' + index, val)
        })
      })
    }
    // User and altUser
    if (filters.user) {
      params.append('user', filters.user)
      params.append('altUser', filters.user)
    }
    return params
  }

  /**
   * Create an object 'data' to make a request by FTS (ElasticSearch).
   * This 'data' must be used when the request is made using the POST method with '/fills/fts'.
   *
   * @param cursorRequest config for the request
   * @param count if the params have to consider the total in the request
   * @returns an object with the values to send in the POST method.
   */
  preparePOSTData (cursorRequest: CursorRequest<FillgridFilters>, count: boolean = false, cursor?: String) {
    const filters = cursorRequest.filters
    const data = {} as any

    data['origin'] = FILLGRID_ORIGIN

    if (count) {
      data['limit'] = 1
      data['count'] = true
    } else {
      data['limit'] = cursorRequest.limit
    }
    if (cursor) data['cursor'] = cursor
    if (filters.view) data['view'] = filters.view
    data['date-type'] = filters.dateType

    const [fromDate, toDate] = this.dateRangeFilter(filters.dates)

    // Date range
    if (!cursorRequest.filters.fullHistory && fromDate) {
      data['from'] = fromDate.format(DATE_FILTER_FORMAT)
    }
    if (toDate) {
      data['to'] = toDate.format(DATE_FILTER_FORMAT)
    }
    if (filters.status) {
      data['worked'] = filters.status === 1
    }
    // Idents
    if (filters.idents.length) {
      filters.idents.forEach((ident, index) => {
        if (ident.ids.length) {
          data['ident' + index] = ident.ids
        }
      })
    }
    if (filters.custom.length) {
      data['values'] = this.getCustomFiltersQuery(filters.custom)
    } else if (filters.fullSearch) {
      data['values'] = cursorRequest.filters.fullSearch
    }
    // User and altUser
    if (filters.user) {
      data['users'] = [filters.user]
      data['altUsers'] = [filters.user]
    }

    if (filters.geoDistance?.kilometers !== 0 &&
      (filters.geoDistance?.location.latitude !== 0 || filters.geoDistance?.location.longitude !== 0)
    ) {
      data['geoDistance'] = filters.geoDistance
    }
    return data
  }

  async getFillDetail (templateId: number, fillName: string, params: any) {
    const queryParams = new URLSearchParams(params)
    const response = await api.get(`${config.apiFillsUrl}templates/${templateId}/fills/${fillName}?${queryParams.toString()}`)
    return response.data
  }

  deleteFill (templateId: number, fillName: string) {
    return new Promise(async (resolve, reject) => {
      try {
        await api.delete(`${config.apiFillsUrl}templates/${templateId}/fills/${fillName}`, {
          headers: {
            'Content-Type': 'application/json'
          },
          data: null
        })
        resolve(true)
      } catch (error) {
        reject(error)
      }
    })
  }

  changeStatusFilltoFNL (templateId: number, fill: any) {
    return new Promise(async (resolve, reject) => {
      try {
        await api.patch(`${config.apiFillsUrl}templates/${templateId}/fills/${fill._name}`, {
          status: 'FNL'
        })
        resolve(true)
      } catch (error) {
        reject(error)
      }
    })
  }

  updateFill (templateId: number, fill: any, changes: any, idents: string[]) {
    // Agregar idents
    idents.forEach(it => {
      changes[it] = fill[it]
    })

    const requestPayload = {
      status: 'OUT',
      values: changes
    }

    this.addActivationOrExpirationDatesToRequest(requestPayload)

    return new Promise(async (resolve, reject) => {
      try {
        await api.patch(`${config.apiFillsUrl}templates/${templateId}/fills/${fill._name}`, requestPayload)
        resolve(true)
      } catch (error) {
        reject(error)
      }
    })
  }

  manageFill (templateId: number, fill: any, changes: any, idents: string[]) {
    const startDateString = moment().utc().add(-30, 'seconds').format(DATE_FILTER_FORMAT)
    const sendDateString = moment().utc().format(DATE_FILTER_FORMAT)

    // Agregar idents
    idents.forEach(it => {
      changes[it] = fill[it]
    })

    return new Promise(async (resolve, reject) => {
      try {
        await api.patch(`${config.apiFillsUrl}templates/${templateId}/fills/${fill._name}`, {
          status: 'RVD',
          editTimes: [1],
          startDate: startDateString,
          sentDate: sendDateString,
          values: changes
        })
        resolve(true)
      } catch (error) {
        reject(error)
      }
    })
  }

  /**
   * Add the special dates (activation and/or expiration) into the request payload
   *
   * @param request to set special dates
   */
  addActivationOrExpirationDatesToRequest (request: any) {
    const changes = request.values
    const isUpdatingActivationDate = this.isUpdatingSpecialDate(EditableFillgridColumnDates.ACTIVATION, changes)
    const isUpdatingExpirationDate = this.isUpdatingSpecialDate(EditableFillgridColumnDates.EXPIRATION, changes)

    if (isUpdatingActivationDate) {
      const activationDate = {
        // we call the toISOString to get a UTC format
        activationDate: new Date(changes[EditableFillgridColumnDates.ACTIVATION]).toISOString()
      }
      Object.assign(request, activationDate)
    }
    if (isUpdatingExpirationDate) {
      const expirationDate = {
        // we call the toISOString to get a UTC format
        expirationDate: new Date(changes[EditableFillgridColumnDates.EXPIRATION]).toISOString()
      }
      Object.assign(request, expirationDate)
    }
  }

  isUpdatingSpecialDate (specialDate: string, changes: any) {
    const propertiesToChange = Object.keys(changes)
    return propertiesToChange.some((property) =>
      property === specialDate
    )
  }

  assignFillToUser (templateId: number, fill: any, user: string) {
    return new Promise(async (resolve, reject) => {
      try {
        await api.patch(`${config.apiFillsUrl}templates/${templateId}/fills/${fill._name}`, {
          status: 'OUT',
          altUser: user
        })
        resolve(true)
      } catch (error) {
        reject(error)
      }
    })
  }

  async resetFillToSourceId (templateId: number, fillName: string, sourceId: number, params: { 'activationDate'?: string, 'expirationDate'?: string }) {
    try {
      const requestUrl = `${config.apiFillsUrl}templates/${templateId}/fills/${fillName}`
      const response = await api.patch(requestUrl, {
        status: FillStatuses.RST,
        sourceId,
        ...params
      })
      return response.data
    } catch (error) {
      LogService.error('Error resetting fill by source id: ' + error)
      throw error
    }
  }

  getCustomFiltersQuery (customFilterItems: any[]) {
    let result = ''
    let prefixOperator = ''
    let midOperator = ''
    let value = ''
    const spaces = new RegExp('\\s')
    customFilterItems.forEach((it, index) => {
      prefixOperator = ''
      midOperator = ':'
      value = ''
      it.value = it.value !== undefined ? it.value : ''

      switch (it.operator) {
        case 'equals':
          value = `"${it.value}"`
          break
        case 'contains':
          value = `*"${it.value}"*`
          break
        case 'notEquals':
          prefixOperator = '-'
          value = `"${it.value}"`
          break
        case 'notContains':
          prefixOperator = '-'
          value = `*"${it.value}"*`
          break
        case 'startsWith':
          value = `"${it.value}"*`
          break
        case 'endsWith':
          value = `*"${it.value}"`
          break
      }

      result += `${it.union}${prefixOperator}${it.field}${midOperator}${value}${it.end}`
    })

    return result
  }

  async getTemplateById (templateId: number): Promise<Template> {
    try {
      const requestUrl = `${config.apiFillsUrl}templates/${templateId}`
      const response = await api.get(requestUrl)
      return response.data
    } catch (error) {
      LogService.error('Error getting template by id :' + error)
      throw error
    }
  }

  getFillgridFileWithoutTypeAttachement (fileName: string, question: string, template: any) {
    const safeFilename = encodeURIComponent(fileName)
    const safeTemplate = encodeURIComponent(String(template))
    const safeQuestion = encodeURIComponent(String(question))
    const requestUrl = `${config.apiFillsUrl}attachments/${safeFilename}?template=${safeTemplate}&question=${safeQuestion}`

    return api.get(requestUrl, { responseType: 'blob' }).then(response => {
      const contentType = (response.headers['content-type'] || 'application/octet-stream') as string
      const mime = contentType.split(';')[0].trim()
      const extFromMime = mime.includes('/') ? mime.split('/').pop() || 'bin' : 'bin'
      const disposition = (response.headers['content-disposition'] || '') as string
      const filenameMatch = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i)
      const downloadedName = decodeURIComponent(filenameMatch?.[1] || filenameMatch?.[2] || fileName)

      const blob = new Blob([response.data], { type: mime })
      const url = URL.createObjectURL(blob)
      return {
        url,
        type: extFromMime,
        filename: downloadedName,
        mime
      }
    })
  }

  getFillgridFileAttachement (fileName: string, question: string, template: any) {
    const requestUrl = `${config.apiFillsUrl}attachments/${fileName}?template=${template}&question=${question}`
    return api.get(requestUrl, { responseType: 'blob' }).then(response => URL.createObjectURL(new Blob([response.data], {
      type: `application/${fileName.split('.').pop()}`
    })))
  }

  /**
   * Get an attachment of type blob response
   *
   * @param name of the attachment to get
   * @returns a blob file
   */
  getAttachmentByNameFromTemplateQuestion (name: string, template: any, question: string): Promise<AxiosResponse<Blob>> {
    return api.get(`${config.apiFillsUrl}attachments/${name}?template=${template}&question=${question}`, { responseType: 'blob' })
  }

  /**
   * Get an attachment from the api fills by its id
   *
   * @param attachmentId of the attachment to get
   * @returns a promise with a URL for the attachment
   */
  async getAttachment (attachmentId: string, template: any, question: any) {
    const params = template === `null` || question === `null` ? `` : `?template=${template}&question=${question}`
    const endpoint = `${config.apiFillsUrl}attachments/${attachmentId}` + params
    return api.get(endpoint, { responseType: 'blob' })
      .then((response) => {
        const contentType = response.headers['Content-Type'] as string
        const blobInstance = new Blob([response.data], { type: contentType })
        return {
          blob: blobInstance,
          type: contentType
        }
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  }
  /**
   * Get a list of normalizers linked to the template
   * @param templateId
   * @returns List of normalizers
   */

  async getNormalizers (templateId: number): Promise<CursorResponse<Normalizer>> {
    const requestUrl = `${config.apiFillsUrl}normalizers?template=${templateId}`
    const response = await api.get(requestUrl)
    return response.data
  }

  getTemplateColumns (template: Template, idents: string[]): FillgridColumn[] {
    const columns: any[] = [new FillgridColumn({}, '', '_checkbox', '', TemplateQuestionsTypes.BOOLEAN, true)]

    const iterationProperties = { questionNumber: 0 }

    template.questions.forEach((question) => {
      this.addTemplateColumn(columns, question, iterationProperties, true, idents)
    })

    columns.push(new FillgridColumn({}, '', '_id', 'Id', TemplateQuestionsTypes.INTEGER, true))
    columns.push(new FillgridColumn({}, '', '_name', 'Name', TemplateQuestionsTypes.TEXT, true).isMetadata())
    columns.push(new FillgridColumn({}, '', '_sourceId', 'Source ID', TemplateQuestionsTypes.INTEGER, true))
    columns.push(new FillgridColumn({}, '', '_user', 'Usuario Ejecutor', TemplateQuestionsTypes.TEXT, true, 'far fa-user').isMetadata())
    columns.push(new FillgridColumn({}, '', '_altUser', 'Usuario Propietario', TemplateQuestionsTypes.TEXT, true, 'far fa-user').isMetadata())
    columns.push(new FillgridColumn({}, '', '_creationDate', 'Creación', TemplateQuestionsTypes.DATETIME, true, 'far fa-calendar-alt').isMetadata())
    columns.push(new FillgridColumn({}, '', '_downloadDate', 'Descarga', TemplateQuestionsTypes.DATETIME, true, 'far fa-calendar-alt').isMetadata())
    columns.push(new FillgridColumn({}, '', '_startDate', 'Edición', TemplateQuestionsTypes.DATETIME, true, 'far fa-calendar-alt').isMetadata())
    columns.push(new FillgridColumn({}, '', '_saveDate', 'Guardado', TemplateQuestionsTypes.DATETIME, true, 'far fa-calendar-alt').isMetadata())
    columns.push(new FillgridColumn({}, '', '_updateDate', 'Recepción', TemplateQuestionsTypes.DATETIME, true, 'far fa-calendar-alt').isMetadata())
    columns.push(new FillgridColumn({}, '', '_receivedDate', 'Actualización', TemplateQuestionsTypes.DATETIME, true, 'far fa-calendar-alt').isMetadata())
    columns.push(new FillgridColumn({}, '', '_activationDate', 'Activación', TemplateQuestionsTypes.DATETIME, true, 'far fa-calendar-alt').isMetadata())
    columns.push(new FillgridColumn({}, '', '_expirationDate', 'Expiración', TemplateQuestionsTypes.DATETIME, true, 'far fa-calendar-alt').isMetadata())
    columns.push(new FillgridColumn({}, '', '_last', 'Último', TemplateQuestionsTypes.DATETIME, true).isMetadata())

    // Get question ref for fillLocation value
    if (template.fillLocation) {
      const questionRef = Template.getQuestionByName(template.fillLocation, template.questions)
      columns.push(new FillgridColumn(questionRef, '', '_fillLocation', '', TemplateQuestionsTypes.LOCATION, true))
    } else {
      columns.push(new FillgridColumn({}, '', '_fillLocation', '', TemplateQuestionsTypes.LOCATION, true))
    }
    // Get question ref for visitedLocation value
    if (template.visitedLocation) {
      const questionRef = Template.getQuestionByName(template.visitedLocation, template.questions)
      columns.push(new FillgridColumn(questionRef, '', '_visitedLocation', '', TemplateQuestionsTypes.LOCATION, true))
    } else {
      columns.push(new FillgridColumn({}, '', '_visitedLocation', '', TemplateQuestionsTypes.LOCATION, true))
    }
    columns.push(new FillgridColumn({}, '', '_priority', '', TemplateQuestionsTypes.TEXT, false))

    template.questions.forEach((question) => {
      this.addTemplateColumn(columns, question, iterationProperties, false, idents)
    })

    return columns
  }

  addTemplateColumn (
    columns: FillgridColumn[],
    question: TemplateQuestion,
    iterationProperties: any,
    identOnly: boolean,
    idents: string[]
  ) {
    if ((question.type === TemplateQuestionsTypes.GROUP || question.type === TemplateQuestionsTypes.ADDRESS) && question.questions) {
      let allowed = true
      if (question.pivot && question.pivot.indexOf('$ROLE') > -1) {
        const pivot = question.pivot
          .replaceAll('$ROLE', '__ROLE__').replaceAll(/\s+/g, '')
          .replaceAll(QUESTION_GROUP_PIVOT_REGEXP, 'true')
          .replaceAll('__ROLE__', userInfoStore.userInfo.role.id.toString())
        try {
          // eslint-disable-next-line
          allowed = eval(pivot)
          allowed = Array.isArray(allowed) ? allowed[0] : allowed
        } catch (e) {
          LogService.error(`Error al evaluar el grupo ${question.id} con pivote ${JSON.stringify(question.pivot)}. Error: ${e}`)
          allowed = false
        }
      }

      if (allowed) {
        question.questions.forEach((subQuestion) => {
          this.addTemplateColumn(columns, subQuestion, iterationProperties, identOnly, idents)
        })
      }
    } else if (question.type !== TemplateQuestionsTypes.LABEL && question.type !== TemplateQuestionsTypes.SIGNATURE) {
      if (identOnly && idents.includes(question.id)) {
        columns.push(Object.freeze(new FillgridColumn(
          question,
          '',
          question.id,
          question.label,
          question.type
        ).isIdent()))
      }

      if (!identOnly && !idents.includes(question.id)) {
        iterationProperties.questionNumber++
        columns.push(Object.freeze(new FillgridColumn(
          question,
          iterationProperties.questionNumber.toString(),
          question.id,
          question.label,
          question.type
        )))
      }
    }
  }

  getFillsColumnsData (columns: FillgridColumn[], data: FillgridEntry[]): any[] {
    if (data === undefined) return []
    const dataResult = data.map((dataEntry) => this.createFillgridRowData(columns, dataEntry))
    return dataResult
  }

  private createFillgridRowData (columns: FillgridColumn[], entryData: FillgridEntry) {
    const object = { _checkbox: true } as any
    columns.forEach((column) => {
      // With this name we attempt to preserve the indexes for the object properties
      const colName: string = FillgridColumn.getColumnName(column.name)
      if (column.base) {
        object[colName] = Object.freeze((entryData as any)[column.name.substring(1)])
      } else if ([TemplateQuestionsTypes.AUDIO, TemplateQuestionsTypes.PHOTO].includes(column.type)) {
        if (entryData.values[column.name]) {
          entryData.values[column.name].url = ''
          entryData.values[column.name].column = column.name
        }
        object[colName] = entryData.values[column.name]
      } else if ([TemplateQuestionsTypes.PDF].includes(column.type)) {
        if (entryData.values[column.name]) {
          entryData.values[column.name] = { column: column.name, name: entryData.values[column.name], url: '' }
        }
        object[colName] = entryData.values[column.name]
      } else if (column.type === TemplateQuestionsTypes.SELECT && column.questionRef.display.type === 'PRIORITY') {
        for (const it of Object.entries(column.questionRef.display.options)) {
          if (entryData.values[column.name] && it[1] === entryData.values[column.name][0]) {
            object['_priority'] = Object.freeze(it[0])
          }
        }
        object[colName] = Object.freeze(entryData.values[column.name])
      } else {
        object[colName] = Object.freeze(entryData.values[column.name])
      }
    })
    // Add status for each fill
    object['status'] = entryData.status
    object['_last'] = entryData.last

    return object
  }

  // Views

  async getViews (templateId: number, pageRequest: PageRequest<FillgridViewFilters>): Promise<PageResponse<any>> {
    const params = new URLSearchParams()
    params.append('limit', pageRequest.size.toString())
    params.append('offset', (pageRequest.page * pageRequest.size).toString())
    if (pageRequest.filters.name) {
      params.append('name', pageRequest.filters.name)
    }

    const response = await api.get(`${config.apiFillsUrl}templates/${templateId}/views`, { params })
    return response.data
  }

  async getViewById (templateId: number, viewId: number): Promise<CustomView> {
    const response = await api.get(`${config.apiFillsUrl}templates/${templateId}/views/${viewId}`)
    return response.data
  }

  async saveView (templateId: number, name: string, metadataColumns: string[], valuesColumns: string[], predetermined: boolean) {
    const response = await api.post(`${config.apiFillsUrl}templates/${templateId}/views`, {
      name: name,
      metadata: metadataColumns,
      values: valuesColumns,
      predetermined
    })
    return response.data
  }

  async updateView (templateId: number, viewId: number, name: string, metadataColumns: string[], valuesColumns: string[], predetermined: boolean) {
    const response = await api.patch(`${config.apiFillsUrl}templates/${templateId}/views/${viewId}`, {
      name: name,
      metadata: metadataColumns,
      values: valuesColumns,
      predetermined
    })
    return response.data
  }

  async deleteView (templateId: number, viewId: number) {
    await api.delete(`${config.apiFillsUrl}templates/${templateId}/views/${viewId}`, {
      headers: {
        'Content-Type': 'application/json'
      },
      data: null
    })
  }

  async timeoutSleep (ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  // Searches

  /**
   * Get all the searches related to a templateId
   *
   * @param templateId template from where get the searches
   * @param pageRequest configurations to get searches
   * @returns response from request
   */
  async getSearches (templateId: number, pageRequest: PageRequest<FillgridSearchFilters>) {
    const params = new URLSearchParams()
    const filters = pageRequest.filters
    params.append('sort', pageRequest.sort)
    params.append('limit', pageRequest.size.toString())
    params.append('offset', (pageRequest.page * pageRequest.size).toString())
    if (filters.name) params.append('name', filters.name)
    if (filters.origin) params.append('origin', filters.origin)
    const endpoint = `${config.apiFillsUrl}templates/${templateId}/searches`
    const response = await api.get(endpoint, { params })
    return response.data
  }

  /**
   * Get a specific custom search by its id
   *
   * @param templateId id of the template
   * @param searchId id of the search to get
   * @returns a CustomSearch response
   */
  async getSearch (templateId: number, searchId: number) {
    const endpoint = `${config.apiFillsUrl}templates/${templateId}/searches/${searchId}`
    const response = await api.get(endpoint)
    return response.data
  }

  /**
   * Create a new search with a determinate configuration
   *
   * @param templateId template to relate the search
   * @param search search configuration
   * @returns response from request
   */
  async createSearch (templateId: number, search: CustomSearch) {
    try {
      const values: {} = this.prepareSearchValues(search, true)
      const endpoint = `${config.apiFillsUrl}templates/${templateId}/searches`
      const response = await api.post(endpoint, values)
      return response.data
    } catch (error) {
      LogService.error('Error creating custom search : ' + error)
      throw error
    }
  }

  /**
   * Update a search with a determinate configuration
   *
   * @param templateId template to relate the search
   * @param search search configuration
   * @returns response from request
   */
  async updateSearch (templateId: number, search: CustomSearch) {
    const values: {} = this.prepareSearchValues(search)
    const endpoint = `${config.apiFillsUrl}templates/${templateId}/searches/${search.id}`
    const response = await api.put(endpoint, values)
    return response.data
  }

  /**
   * Delete a search with 'searchId' id from the template with 'templateId' id
   *
   * @param templateId ide of the template from where delete the seach
   * @param searchId id of the search to delete
   * @returns API response
   */
  async deleteSearch (templateId:number, searchId: number) {
    const endpoint = `${config.apiFillsUrl}templates/${templateId}/searches/${searchId}`
    const response = await api.delete(endpoint, {
      headers: {
        'Content-Type': 'application/json'
      },
      data: null
    })
    return response.data
  }

  /**
   * Create a URLSearchParams with a configuration to make a POST, PUT or PATCH search request
   *
   * @param search to manage its search params
   * @param post to indicate if the params will be used in a POST request
   * @returns an object with search values to POST, PUT or PATCH request
   */
  prepareSearchValues (search: CustomSearch, post: boolean = false): {} {
    let fromString = ''
    if (search.from) {
      const from = new Date(search.from)
      from.setHours(0, 0, 0, 0)
      fromString = from.toISOString().substring(0, DATE_FILTER_FORMAT.length)
    }
    let toString = ''
    if (search.to) {
      const toDate = new Date(search.to)
      const today = new Date()
      if (DateUtils.areDatesSameDay(today, toDate)) {
        search.to = undefined // to preserve the same performance that the search in fillgrid
      } else {
        toDate.setHours(23, 59, 59, 0)
      }
      toString = toDate.toISOString().substring(0, DATE_FILTER_FORMAT.length)
    }

    const values: any = {}
    if (post || search.name) values.name = search.name // required only for POST
    if (post || search.origin) values.origin = search.origin // required only for POST
    if (search.status !== undefined && search.status !== null) values.status = search.status // optional
    if (search.worked !== undefined && search.worked !== null) values.worked = search.worked // optional
    if (search.ident0 && search.ident0.length) values.ident0 = search.ident0 // optional
    if (search.ident1 && search.ident1.length) values.ident1 = search.ident1 // optional
    if (search.ident2 && search.ident2.length) values.ident2 = search.ident2 // optional
    if (search.locationStatus && search.locationStatus.length) values.locationStatus = search.locationStatus // optional
    if (search.view && search.view > 0) values.view = search.view // optional
    if (search.from && fromString) values.from = fromString // optional
    if (search.to && toString) values.to = toString // optional
    if (search.dateType) values.dateType = search.dateType // optional

    const userORAltUser = search.user && search.user.length // this user array should have always one item
      ? `user:${search.user} OR altUser:${search.user}`
      : ''

    if (search.values && search.values.length) {
      values.values = userORAltUser
        ? `(${search.values}) AND (${userORAltUser})`
        : search.values
    } else if (search.fullValues) {
      values.values = userORAltUser
        ? `(${search.fullValues}) AND (${userORAltUser})`
        : search.fullValues
    } else {
      values.values = userORAltUser
    }

    if (search.geoDistance?.kilometers !== 0 && (search.geoDistance?.location.latitude !== 0 || search.geoDistance?.location.longitude !== 0)) {
      values.geoDistance = search.geoDistance
    }

    return values
  }

  /**
   * Convert a FilgridFilters object to a CustomSearch object
   *
   * @param filters fillgridFilters to convert
   * @param view viewId to set
   * @returns CustomSearch object equivalent to the FillgridFilters
   */
  fillgridFiltersToCustomSearch (filters: FillgridFilters, view?: number): CustomSearch {
    const search: CustomSearch = CustomSearch.getInstance()
    search.name = filters.name
    if (filters.status === 1) {
      search.worked = true
    } else if (filters.status === 2) {
      search.worked = false
    } else {
      search.worked = undefined
    }
    search.user = filters.user ? [filters.user] : undefined
    search.altUser = filters.altuser ? [filters.altuser] : undefined
    search.ident0 = filters.idents && filters.idents.length > 0 ? filters.idents[0].ids : undefined
    search.ident1 = filters.idents && filters.idents.length > 1 ? filters.idents[1].ids : undefined
    search.ident2 = filters.idents && filters.idents.length > 2 ? filters.idents[2].ids : undefined
    if (view !== undefined) search.view = view
    search.origin = filters.origin // FILL_GRID or DATAFEED
    if (filters.dates.length) {
      if (filters.dates[0] !== null) search.from = filters.dates[0].toISOString()
      if (filters.dates[1] !== null) search.to = filters.dates[1].toISOString()
    }
    search.dateType = filters.dateType
    search.values = this.getCustomFiltersQuery(filters.custom)
    search.fullValues = filters.fullSearch

    if (filters.geoDistance?.kilometers !== 0 && (filters.geoDistance?.location.latitude !== 0 || filters.geoDistance?.location.longitude !== 0)) {
      search.geoDistance = filters.geoDistance
    }
    return search
  }

  /** To save in the localStorage the last search */

  saveCustomSearchInLocalStorage (templateId: number, customSearch: CustomSearch) {
    const templateFilters = {
      templateId,
      filters: customSearch
    }
    localStorage.setItem(LAST_FILLGRID_SEARCH, JSON.stringify(templateFilters))
  }

  loadCustomSearchFromLocalStorage (): { templateId: number, filters: CustomSearch } | undefined {
    const lastFillgridSearch = localStorage.getItem(LAST_FILLGRID_SEARCH)
    const templateFilters = lastFillgridSearch !== null
      ? JSON.parse(lastFillgridSearch)
      : undefined
    return templateFilters
  }

  clearCustomSearchInLocalStorage () {
    localStorage.removeItem(LAST_FILLGRID_SEARCH)
  }

  /** Prototype methods */
  async getVideoPresignedUrl (videoPath: string): Promise<{ url: string}> {
    try {
      const response = await api.post(
        config.videosLambdaUrl,
        { path: videoPath }
      )
      return response.data
    } catch (error) {
      LogService.error(`Error getting video presigned URL: ${error}`)
      throw error
    }
  }
}

export default new FillgridService()
